import {
  UNLOAD_PHOTO_RULE,
  classifyShareError,
  dateOnly,
  formatMonthDayDow,
  formatMonthDayDowTime,
  normalizeTime,
  type ShareError,
} from '@indipia/shared-features';
import type {
  ReceiptShareReceipt,
  ReceiptShareTarget,
  ReceiptShareView,
  ReceiptSubmitResult,
} from '../api/receiptShareApi';

/**
 * C-4-2 반장 입고 사진 등록 — 화면 로직(순수 함수). 화면 문구는 이 파일 한 곳에 모은다(지시서 §13 M1:
 * 940 에 없는 문구는 문구 초안이며 기획 확정 시 상수만 교체).
 */

// ───────── 문구 ─────────
export const RECEIPT_TEXT = {
  documentTitle: '입고 확인',
  heading: '입고 확인',
  photoLabel: '하차사진',
  photoHint: '최대 5장',
  photoRuleDesc: '최대 5장, 한 장당 20MB',
  photoAddLabel: '사진 추가',
  photoEmptyHint: '하차사진을 1장 이상 올려 주세요.',
  qtyLabel: '수량 확인',
  qtyNormal: '이상 없음',
  qtyIssue: '부족 · 파손 있음',
  qtyIssueChip: '부족 · 파손 있음',
  issueNoteLabel: '부족 · 파손 내용',
  issueNotePlaceholder: '예) 엘보 20개 중 15개만 도착, 1개 파손',
  issueNotifyAlert: '보내면 담당자에게 바로 알림이 가요.',
  issueTypeLabel: '부족 · 파손 구분',
  issueTypeHint: '해당하는 것을 모두 골라 주세요',
  issueTypeShortage: '부족',
  issueTypeDefect: '파손 · 불량',
  required: '필수',
  btnReady: '입고 완료',
  btnNoPhoto: '하차사진을 1장 이상 올려 주세요',
  btnNoQty: '수량 확인을 골라 주세요',
  btnNoNote: '부족 · 파손 내용을 입력해 주세요',
  btnNoIssueType: '부족 · 파손 구분을 골라 주세요',
  retry: '다시 보내기',
  doneTitle: '입고 완료로 등록했어요',
  doneDesc: '담당자 공정표에 입고 완료가 표시돼요.',
  doneFooter: '사진을 잘못 올렸으면 담당자에게 연락해 주세요.',
  noticeTitle: '지금은 입고를 등록할 수 없어요',
  noticeStaffMissing: '현장 담당자가 지정되지 않았어요. 담당자에게 연락해 주세요.',
  noticeNotAllowed: '지금은 입고를 등록할 수 없는 상태예요. 담당자에게 문의해 주세요.',
  errPhotoCount: '하차사진은 1~5장을 올려 주세요.',
  errPhotoBatch: '사진은 최대 5장까지 올릴 수 있어요.',
  errPhotoSize: '사진은 한 장당 20MB까지 올릴 수 있어요.',
  errPhotoType: '사진 파일만 올릴 수 있어요.',
  errPayload: '사진 용량이 너무 커요. 사진 수를 줄이거나 더 작은 사진으로 올려 주세요.',
  errNoteRequired: '부족 · 파손 내용을 입력해 주세요.',
  errIssueType: '부족 · 파손 구분을 골라 주세요.',
  errValidation: '입력한 내용을 확인해 주세요.',
  errPlanner: '입고 기록을 저장하지 못했어요. 잠시 후 다시 보내 주세요.',
  errNetwork: '입고를 보내지 못했어요. 네트워크를 확인하고 다시 보내 주세요.',
  errServer: '잠시 후 다시 보내 주세요.',
  // 요청 카드·완료 화면 라벨과 폼 보조 문구
  rowSite: '현장',
  rowWorkType: '공종',
  rowVendor: '거래처',
  rowArrival: '도착 예정',
  rowDriver: '기사',
  headerForeman: (name: string) => `시공파트너 ${name}`,
  rowItems: '품목',
  rowReceiptAt: '등록 일시',
  rowReceiptDate: '등록일',
  rowPhotoCount: '하차사진',
  rowQtyChecked: '수량 확인',
  photoCountValue: (n: number) => `${n}장`,
  photoSelectedLive: (n: number) => `하차사진 ${n}장 선택됨`,
  uploadProgress: (pct: number) => `${pct}% 올리는 중`,
  qtyNormalMark: '✓ ',
  qtyIssueMark: '! ',
} as const;

export const ISSUE_NOTE_MAX = 300;
export const PHOTO_MIN = 1;

export type QtyChecked = 'NORMAL' | 'ISSUE';

/** 입고 이슈 구분(ZEU_ORDER_ISSUE_TYPE) — 수량 확인이 ISSUE 일 때 복수 선택. 서버가 작업보고 이슈 유형(자재부족·자재불량)으로 옮긴다. */
export type IssueType = 'SHORTAGE' | 'DEFECT';

export const ISSUE_TYPE_OPTIONS: ReadonlyArray<{ code: IssueType; label: string }> = [
  { code: 'SHORTAGE', label: RECEIPT_TEXT.issueTypeShortage },
  { code: 'DEFECT', label: RECEIPT_TEXT.issueTypeDefect },
];

// ───────── 조회 결과 뷰 모델 ─────────
export interface ReceivedSummary {
  /** 등록 일시 표시값(GET) — 없거나 해석 불가면 null(줄 숨김) */
  receiptAtText: string | null;
  /** 등록일 표시값 */
  receiptDateText: string | null;
  photoCnt: number | null;
  qtyCheckedCode: string | null;
}

export type ScreenKind = 'SESSION' | 'LINK' | 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'NETWORK' | 'SERVER' | 'UNKNOWN';

export type ReceiptView =
  | { t: 'open'; target: ReceiptShareTarget }
  | { t: 'received'; summary: ReceivedSummary | null }
  | { t: 'screen'; kind: ScreenKind; companyName?: string }
  | { t: 'noStaff' }
  | { t: 'notAllowed' };

const UNKNOWN_VIEW: ReceiptView = { t: 'screen', kind: 'UNKNOWN' };

function safeFormat<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

function summaryOf(r: {
  receiptDtm?: string | null;
  receiptDate?: string | null;
  photoCnt?: number | null;
  qtyCheckedCode?: string | null;
}): ReceivedSummary {
  const atText = r.receiptDtm
    ? safeFormat(() => formatMonthDayDowTime(dateOnly(r.receiptDtm as string), normalizeTime((r.receiptDtm as string).slice(11, 16))))
    : null;
  const dateText = r.receiptDate
    ? safeFormat(() => formatMonthDayDow(dateOnly(r.receiptDate as string)))
    : r.receiptDtm
      ? safeFormat(() => formatMonthDayDow(dateOnly(r.receiptDtm as string)))
      : null;
  return {
    receiptAtText: atText,
    receiptDateText: dateText,
    photoCnt: typeof r.photoCnt === 'number' ? r.photoCnt : null,
    qtyCheckedCode: r.qtyCheckedCode === 'NORMAL' || r.qtyCheckedCode === 'ISSUE' ? r.qtyCheckedCode : null,
  };
}

/** 입고 GET 응답 → 화면 뷰(§10.2). 필수 신호(state·target) 부재·약속 밖 값은 UNKNOWN. 폴백 금지. */
export function toReceiptView(data: ReceiptShareView | null | undefined): ReceiptView {
  if (!data || typeof data.state !== 'string') return UNKNOWN_VIEW;
  switch (data.state) {
    case 'OPEN':
      return data.target ? { t: 'open', target: data.target } : UNKNOWN_VIEW;
    case 'RECEIVED': {
      const r: ReceiptShareReceipt | null | undefined = data.receipt;
      return { t: 'received', summary: r ? summaryOf(r) : null };
    }
    case 'CLOSED':
      return { t: 'screen', kind: 'CLOSED' };
    case 'EXPIRED':
      if (!data.target) return UNKNOWN_VIEW;
      return { t: 'screen', kind: 'EXPIRED', companyName: data.target.companyName || undefined };
    default:
      return UNKNOWN_VIEW;
  }
}

/** 입고 GET 오류 → 화면 뷰(§10.3). 화면으로 못 옮기는 오류(재시도 대상 포함)는 ShareError 그대로. */
export function loadErrorToView(err: ShareError): ReceiptView | null {
  if (err.code === 'ORDER_SITE_STAFF_MISSING') return { t: 'noStaff' };
  if (err.code === 'ORDER_RECEIPT_NOT_ALLOWED') return { t: 'notAllowed' };
  if (err.kind === 'ALREADY_RECEIVED' || err.code === 'ORDER_ALREADY_RECEIVED') return { t: 'received', summary: null };
  return null;
}

/** 로딩 실패 kind → 전체 화면 종류. 화면 대상이 아닌 kind(VALIDATION 등)는 UNKNOWN. */
export function screenKindOf(kind: ShareError['kind']): ScreenKind {
  switch (kind) {
    case 'SESSION':
    case 'LINK':
    case 'CLOSED':
    case 'EXPIRED':
    case 'CHANGED':
    case 'NETWORK':
    case 'SERVER':
      return kind;
    default:
      return 'UNKNOWN';
  }
}

// ───────── 표시 ─────────
export function arrivalText(t: ReceiptShareTarget): string | null {
  // 둘 다 없으면 줄 숨김(배송정보 회신 전 자재 선도착은 정상). 한쪽만 있어도 만들어 채우지 않고 숨김.
  if (!t.arrivalDate || !t.arrivalTime) return null;
  return safeFormat(() => formatMonthDayDowTime(dateOnly(t.arrivalDate as string), normalizeTime(t.arrivalTime as string)));
}

export function itemText(i: { itemName: string; itemSpec?: string | null; itemQty?: number | string | null; itemUnit?: string | null }): string {
  const spec = i.itemSpec ? ` ${i.itemSpec}` : '';
  const qty = i.itemQty != null && i.itemQty !== '' ? ` ${i.itemQty}${i.itemUnit ?? ''}` : '';
  return `${i.itemName}${spec}${qty}`;
}

export function qtyCheckedText(code: string | null): string | null {
  if (code === 'NORMAL') return RECEIPT_TEXT.qtyNormal;
  if (code === 'ISSUE') return RECEIPT_TEXT.qtyIssue;
  return null;
}

// ───────── 검증 ─────────
export interface ReceiptDraftState {
  files: File[];
  qty: QtyChecked | null;
  issueNote: string;
  /** 수량 확인이 ISSUE 일 때 선택한 구분(복수). NORMAL 이면 무시 */
  issueTypes: IssueType[];
}

export interface ReceiptValidation {
  ready: boolean;
  /** 제출 버튼 라벨(비활성 사유 또는 '입고 완료') */
  buttonLabel: string;
  /** 제출 버튼 variant — 수량 이슈면 danger(940 목업) */
  variant: 'primary' | 'danger';
}

function hasValidFiles(files: File[]): boolean {
  if (files.length < PHOTO_MIN || files.length > UNLOAD_PHOTO_RULE.maxCount) return false;
  return files.every((f) => f.size <= UNLOAD_PHOTO_RULE.maxBytes);
}

export function validateReceipt(d: ReceiptDraftState): ReceiptValidation {
  const variant = d.qty === 'ISSUE' ? 'danger' : 'primary';
  if (!hasValidFiles(d.files)) return { ready: false, buttonLabel: RECEIPT_TEXT.btnNoPhoto, variant };
  if (d.qty == null) return { ready: false, buttonLabel: RECEIPT_TEXT.btnNoQty, variant };
  if (d.qty === 'ISSUE') {
    if (d.issueTypes.length < 1) return { ready: false, buttonLabel: RECEIPT_TEXT.btnNoIssueType, variant };
    const note = d.issueNote.trim();
    if (note.length < 1 || note.length > ISSUE_NOTE_MAX) {
      return { ready: false, buttonLabel: RECEIPT_TEXT.btnNoNote, variant };
    }
  }
  return { ready: true, buttonLabel: RECEIPT_TEXT.btnReady, variant };
}

// ───────── 전송 본문 ─────────
/** multipart 본문. NORMAL 이면 issueNote·issueTypeCodes 필드 자체를 넣지 않는다(ISSUE 는 구분마다 issueTypeCodes 반복). Content-Type 은 지정하지 않는다. */
export function buildReceiptFormData(
  submitUuid: string,
  d: { files: File[]; qty: QtyChecked; issueNote: string; issueTypes: IssueType[] },
): FormData {
  const form = new FormData();
  form.append('submitUuid', submitUuid);
  form.append('qtyCheckedCode', d.qty);
  if (d.qty === 'ISSUE') {
    form.append('issueNote', d.issueNote.trim());
    d.issueTypes.forEach((t) => form.append('issueTypeCodes', t));
  }
  d.files.forEach((f) => form.append('photoFiles', f));
  return form;
}

/** 화면 수명 동안 고정할 제출 식별자. randomUUID 가 없으면 null(대체 난수 금지 → 호출 화면이 UNKNOWN 처리). */
export function newSubmitUuid(): string | null {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c && typeof c.randomUUID === 'function' ? c.randomUUID() : null;
}

// ───────── 제출 결과 매핑 ─────────
export type SubmitOutcome =
  | { t: 'done'; summary: ReceivedSummary }
  | { t: 'view'; view: ReceiptView }
  | { t: 'field'; photoError?: string; noteError?: string; typeError?: string; formError?: string }
  | { t: 'form'; message: string };

/** 2xx 응답 → 완료. state 가 RECEIVED 가 아니면 UNKNOWN. 요약 값이 null 이면 해당 줄만 숨김(S8). */
export function mapSubmitSuccess(data: ReceiptSubmitResult | null | undefined): SubmitOutcome {
  if (!data || data.state !== 'RECEIVED') return { t: 'view', view: UNKNOWN_VIEW };
  return { t: 'done', summary: summaryOf({ receiptDate: data.receiptDate, photoCnt: data.photoCnt, qtyCheckedCode: data.qtyCheckedCode }) };
}

export function mapSubmitError(err: ShareError, companyName?: string): SubmitOutcome {
  switch (err.code) {
    case 'RECEIPT_PHOTO_COUNT_INVALID':
      return { t: 'field', photoError: RECEIPT_TEXT.errPhotoCount };
    case 'BATCH_LIMIT_EXCEEDED':
      return { t: 'field', photoError: RECEIPT_TEXT.errPhotoBatch };
    case 'FILE_SIZE_EXCEEDED':
      return { t: 'field', photoError: RECEIPT_TEXT.errPhotoSize };
    case 'FILE_TYPE_NOT_ALLOWED':
      return { t: 'field', photoError: RECEIPT_TEXT.errPhotoType };
    case 'RECEIPT_ISSUE_NOTE_REQUIRED':
      return { t: 'field', noteError: RECEIPT_TEXT.errNoteRequired };
    case 'ORDER_ALREADY_RECEIVED':
      return { t: 'view', view: { t: 'received', summary: null } };
    case 'ORDER_RECEIPT_NOT_ALLOWED':
      return { t: 'view', view: { t: 'notAllowed' } };
    case 'ORDER_SITE_STAFF_MISSING':
      return { t: 'view', view: { t: 'noStaff' } };
    case 'RECEIPT_PLANNER_UNAVAILABLE':
      return { t: 'form', message: RECEIPT_TEXT.errPlanner };
    case 'VALIDATION_ERROR':
      // submitUuid 관련이면 화면 오류(UNKNOWN), 그 밖은 일반 안내
      if (err.field === 'submitUuid') return { t: 'view', view: UNKNOWN_VIEW };
      if (err.field === 'issueTypeCodes') return { t: 'field', typeError: RECEIPT_TEXT.errIssueType };
      return { t: 'field', formError: RECEIPT_TEXT.errValidation };
    default:
      break;
  }
  switch (err.kind) {
    case 'PAYLOAD':
      return { t: 'form', message: RECEIPT_TEXT.errPayload };
    case 'NETWORK':
      return { t: 'form', message: RECEIPT_TEXT.errNetwork };
    case 'SERVER':
      return { t: 'form', message: RECEIPT_TEXT.errServer };
    case 'VALIDATION':
      return { t: 'field', formError: RECEIPT_TEXT.errValidation };
    case 'ALREADY_RECEIVED':
      return { t: 'view', view: { t: 'received', summary: null } };
    case 'EXPIRED':
      return { t: 'view', view: { t: 'screen', kind: 'EXPIRED', companyName } };
    case 'SESSION':
    case 'LINK':
    case 'CLOSED':
    case 'CHANGED':
      return { t: 'view', view: { t: 'screen', kind: err.kind } };
    default:
      return { t: 'view', view: UNKNOWN_VIEW };
  }
}

/** 제출 오류 원본 → 매핑(분류 포함). */
export function mapSubmitException(e: unknown, companyName?: string): SubmitOutcome {
  return mapSubmitError(classifyShareError(e), companyName);
}

/** 다시 보내기 안내를 붙일 오류인지(응답 없음·5xx·503). 같은 submitUuid 로 재전송. */
export function isRetryableForm(outcome: SubmitOutcome): boolean {
  // 413(용량 초과)은 같은 사진으로 다시 보내도 같은 결과라 [다시 보내기]를 숨긴다(사진을 고친 뒤 제출 버튼 사용).
  return outcome.t === 'form' && outcome.message !== RECEIPT_TEXT.errPayload;
}
